import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  EconomicEvent,
  EconomicEventCategory,
  Country,
  IMPORTANCE_LEVELS,
} from '../../constants/economicCalendarData';
import { EVENT_CATEGORIES } from '../../constants/economicCalendarData';
import { economicCalendarAPI } from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import SourceCombobox from './SourceCombobox';

interface EventFormProps {
  onSubmit: (data: any) => void;
  initialData?: EconomicEvent;
}

interface EventFormValues {
  eventName: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  country: string;
  timezone: string;
  category: EconomicEventCategory;
  importance: 'low' | 'medium' | 'high';
  actualValue: string;
  forecastValue: string;
  previousValue: string;
  sourceUrl: string;
}

const timeFormats = ['HH:mm', 'HH:mm:ss'];

const EventForm = ({ onSubmit, initialData }: EventFormProps) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('');

  const [formData, setFormData] = useState<EventFormValues>(() => ({
    eventName: initialData?.eventName || '',
    description: initialData?.description || '',
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
    startTime: initialData?.startTime || '',
    endTime: initialData?.endTime || '',
    country: initialData?.country || '',
    timezone: initialData?.timezone || '',
    category: initialData?.category || ('other' as EconomicEventCategory),
    importance: initialData?.importance || 'medium',
    actualValue: initialData?.actualValue || '',
    forecastValue: initialData?.forecastValue || '',
    previousValue: initialData?.previousValue || '',
    sourceUrl: initialData?.sourceUrl || '',
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        eventName: initialData.eventName || '',
        description: initialData.description || '',
        startDate: initialData.startDate || '',
        endDate: initialData.endDate || '',
        startTime: initialData.startTime || '',
        endTime: initialData.endTime || '',
        country: initialData.country || '',
        timezone: initialData.timezone || '',
        category: initialData.category || ('other' as EconomicEventCategory),
        importance: initialData.importance || 'medium',
        actualValue: initialData.actualValue || '',
        forecastValue: initialData.forecastValue || '',
        previousValue: initialData.previousValue || '',
        sourceUrl: initialData.sourceUrl || '',
      });
      setErrors({});
    }
  }, [initialData]);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!initialData || countries.length === 0) return;

    const country = countries.find(c => c.name === initialData.country);
    setSelectedCurrency(initialData.currency || country?.currency || '');
  }, [countries, initialData]);

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await economicCalendarAPI.getCountries();
      const rawCountries = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.countries)
          ? response.data.countries
          : [];

      if (rawCountries.length > 0) {
        const formattedCountries = rawCountries
          .map((country: any) => ({
            name: country.name || country.countryName || '',
            phoneCode: country.phoneCode || '',
            currency: country.currency || '',
            timezone: Array.isArray(country.timezone)
              ? country.timezone
              : country.timezone
                ? [country.timezone]
                : [],
          }))
          .filter((country: Country) => country.name);
        setCountries(formattedCountries);
      }
    } catch (error) {
      toast.error('Failed to fetch countries');
    } finally {
      setLoadingCountries(false);
    }
  };

  const validateForm = (
    dataToValidate: EventFormValues = formData
  ): boolean => {
    const newErrors: Record<string, string> = {};

    if (!dataToValidate.eventName?.trim()) {
      newErrors.eventName = 'Event name is required';
    } else if (dataToValidate.eventName.length > 50) {
      newErrors.eventName = 'Event name cannot exceed 50 characters';
    }

    const validateEconomicValue = (val: string): string | null => {
      if (!val || !val.trim()) return null;
      const trimmed = val.trim();
      const numericValue = Number(trimmed);

      if (!Number.isFinite(numericValue)) {
        return 'Please enter a valid number';
      }
      if (numericValue < 1 || numericValue > 999) {
        return 'Value must be between 1 and 999';
      }

      return null;
    };

    const actualErr = validateEconomicValue(dataToValidate.actualValue);
    if (actualErr) newErrors.actualValue = actualErr;

    const forecastErr = validateEconomicValue(dataToValidate.forecastValue);
    if (forecastErr) newErrors.forecastValue = forecastErr;

    const previousErr = validateEconomicValue(dataToValidate.previousValue);
    if (previousErr) newErrors.previousValue = previousErr;

    if (!dataToValidate.startDate) {
      newErrors.startDate = 'Start date is required';
    } else {
      const today = moment().startOf('day');
      const start = moment(dataToValidate.startDate);
      if (!start.isValid()) {
        newErrors.startDate = 'Enter a valid start date';
      } else if (start.isBefore(today, 'day')) {
        newErrors.startDate = 'Start date cannot be in the past';
      } else if (
        dataToValidate.endDate &&
        moment(dataToValidate.endDate).isBefore(start, 'day')
      ) {
        newErrors.startDate = 'End date must be same or later than start date';
      }
    }
    if (!dataToValidate.endDate) {
      newErrors.endDate = 'End date is required';
    } else {
      const end = moment(dataToValidate.endDate);
      if (!end.isValid()) {
        newErrors.endDate = 'Enter a valid end date';
      } else if (
        dataToValidate.startDate &&
        end.isBefore(moment(dataToValidate.startDate), 'day')
      ) {
        newErrors.endDate = 'End date must be same or later than start date';
      }
    }
    const sameDay =
      dataToValidate.startDate &&
      dataToValidate.endDate &&
      moment(dataToValidate.startDate).isSame(
        moment(dataToValidate.endDate),
        'day'
      );

    if (!dataToValidate.startTime) {
      newErrors.startTime = 'Start time is required';
    } else if (sameDay && dataToValidate.endTime) {
      const start = moment(dataToValidate.startTime, timeFormats, true);
      const end = moment(dataToValidate.endTime, timeFormats, true);
      if (start.isValid() && end.isValid() && !end.isAfter(start)) {
        newErrors.startTime = 'Start time must be before end time';
      }
    }

    if (!dataToValidate.endTime) {
      newErrors.endTime = 'End time is required';
    } else if (sameDay && dataToValidate.startTime) {
      const start = moment(dataToValidate.startTime, timeFormats, true);
      const end = moment(dataToValidate.endTime, timeFormats, true);
      if (start.isValid() && end.isValid() && !end.isAfter(start)) {
        newErrors.endTime = 'End time must be after start time';
      }
    }

    if (!dataToValidate.country?.trim()) {
      newErrors.country = 'Country is required';
    }
    if (!dataToValidate.timezone?.trim()) {
      newErrors.timezone = 'Timezone is required';
    }
    if (!dataToValidate.category) {
      newErrors.category = 'Category is required';
    }
    if (!dataToValidate.importance) {
      newErrors.importance = 'Impact level is required';
    }
    if (dataToValidate.sourceUrl?.trim()) {
      try {
        new URL(dataToValidate.sourceUrl);
      } catch {
        newErrors.sourceUrl = 'Please enter a valid URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof EventFormValues, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (Object.keys(errors).length > 0) {
        validateForm(updated);
      }
      return updated;
    });
  };

  const handleCountryChange = (countryName: string) => {
    const country = countries.find(c => c.name === countryName);
    const timezoneValue = country
      ? Array.isArray(country.timezone)
        ? country.timezone[0] || ''
        : country.timezone || ''
      : '';

    setSelectedCurrency(country?.currency || '');

    setFormData(prev => {
      const updated = {
        ...prev,
        country: countryName,
        timezone: String(timezoneValue),
      };
      if (Object.keys(errors).length > 0) {
        validateForm(updated);
      }
      return updated;
    });
  };

  const filteredCategories = EVENT_CATEGORIES;
  const categoryLabel =
    filteredCategories.find(option => option.value === formData.category)
      ?.label || '';
  const filteredImportance = IMPORTANCE_LEVELS.filter(
    option => option.value !== 'all'
  );
  const importanceLabel =
    filteredImportance.find(option => option.value === formData.importance)
      ?.label || '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const country = countries.find(c => c.name === formData.country);
    const transformedData = {
      title: formData.eventName,
      description: formData.description || '',
      country: country?.name || formData.country || '',
      start_date: formData.startDate,
      end_date: formData.endDate,
      start_time: formData.startTime,
      end_time: formData.endTime,
      timezone: formData.timezone,
      impact_level: formData.importance,
      category: formData.category,
      actual_value: formData.actualValue || '',
      forecast_value: formData.forecastValue || '',
      previous_value: formData.previousValue || '',
      source_url: formData.sourceUrl || '',
      status: 'scheduled',
      currency: selectedCurrency || country?.currency || '',
    };

    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Event Name */}
      <div className="space-y-2">
        <Label htmlFor="eventName">Event Name *</Label>
        <Input
          id="eventName"
          placeholder="e.g., Non-Farm Payrolls"
          maxLength={50}
          value={formData.eventName}
          onChange={e => handleChange('eventName', e.target.value)}
        />
        {errors.eventName && (
          <p className="text-sm text-red-500">{errors.eventName}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          placeholder="Brief description of the event"
          rows={3}
          value={formData.description}
          onChange={e => handleChange('description', e.target.value)}
        />
      </div>

      {/* Date Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startDate">Start Date *</Label>
          <DatePicker
            id="startDate"
            value={formData.startDate}
            onChange={value => handleChange('startDate', value)}
            placeholder="Pick a start date"
            hasError={!!errors.startDate}
            disabledBefore={moment().startOf('day').toDate()}
          />
          {errors.startDate && (
            <p className="text-sm text-red-500">{errors.startDate}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="endDate">End Date *</Label>
          <DatePicker
            id="endDate"
            value={formData.endDate}
            onChange={value => handleChange('endDate', value)}
            placeholder="Pick an end date"
            hasError={!!errors.endDate}
            disabledBefore={
              formData.startDate
                ? moment(formData.startDate).startOf('day').toDate()
                : undefined
            }
          />
          {errors.endDate && (
            <p className="text-sm text-red-500">{errors.endDate}</p>
          )}
        </div>
      </div>

      {/* Time Range */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startTime">Start Time *</Label>
          <TimePicker
            id="startTime"
            name="startTime"
            value={formData.startTime}
            onChange={value => handleChange('startTime', value)}
            hasError={!!errors.startTime}
          />
          {errors.startTime && (
            <p className="text-sm text-red-500">{errors.startTime}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="endTime">End Time *</Label>
          <TimePicker
            id="endTime"
            name="endTime"
            value={formData.endTime}
            onChange={value => handleChange('endTime', value)}
            hasError={!!errors.endTime}
          />
          {errors.endTime && (
            <p className="text-sm text-red-500">{errors.endTime}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="country">Country *</Label>
          <SourceCombobox
            id="country"
            items={countries.map(country => country.name)}
            placeholder="Select country"
            value={formData.country}
            onChange={value => handleCountryChange(value)}
            disabled={loadingCountries}
            emptyText="No countries found."
          />
          {errors.country && (
            <p className="text-sm text-red-500">{errors.country}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone *</Label>
          <Input
            id="timezone"
            placeholder="Timezone"
            value={formData.timezone}
            readOnly
            className="bg-muted"
          />
          {errors.timezone && (
            <p className="text-sm text-red-500">{errors.timezone}</p>
          )}
        </div>
      </div>

      {/* Category + Impact Level */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category *</Label>
          <SourceCombobox
            id="category"
            items={filteredCategories.map(option => option.label)}
            placeholder="Select category"
            value={categoryLabel}
            onChange={value => {
              const option = filteredCategories.find(
                item => item.label === value
              );
              if (option) {
                handleChange('category', option.value as EconomicEventCategory);
              }
            }}
            emptyText="No categories found."
          />
          {errors.category && (
            <p className="text-sm text-red-500">{errors.category}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="importance">Impact Level *</Label>
          <SourceCombobox
            id="importance"
            items={filteredImportance.map(option => option.label)}
            placeholder="Select impact level"
            value={importanceLabel}
            onChange={value => {
              const option = filteredImportance.find(
                item => item.label === value
              );
              if (option) {
                handleChange(
                  'importance',
                  option.value as 'low' | 'medium' | 'high'
                );
              }
            }}
            emptyText="No impact levels found."
          />
          {errors.importance && (
            <p className="text-sm text-red-500">{errors.importance}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="actualValue">Actual Value</Label>
          <Input
            id="actualValue"
            placeholder="e.g., 200K, 0.5%, 100"
            value={formData.actualValue}
            onChange={e => handleChange('actualValue', e.target.value)}
          />
          {errors.actualValue && (
            <p className="text-sm text-red-500">{errors.actualValue}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="forecastValue">Forecast Value</Label>
          <Input
            id="forecastValue"
            placeholder="e.g., 180K, 0.4%, 90"
            value={formData.forecastValue}
            onChange={e => handleChange('forecastValue', e.target.value)}
          />
          {errors.forecastValue && (
            <p className="text-sm text-red-500">{errors.forecastValue}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="previousValue">Previous Value</Label>
          <Input
            id="previousValue"
            placeholder="e.g., 175K, 0.3%, 85"
            value={formData.previousValue}
            onChange={e => handleChange('previousValue', e.target.value)}
          />
          {errors.previousValue && (
            <p className="text-sm text-red-500">{errors.previousValue}</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="sourceUrl">Source URL</Label>
        <Input
          id="sourceUrl"
          type="url"
          placeholder="https://example.com/source"
          value={formData.sourceUrl}
          onChange={e => handleChange('sourceUrl', e.target.value)}
        />
        {errors.sourceUrl && (
          <p className="text-sm text-red-500">{errors.sourceUrl}</p>
        )}
      </div>
    </form>
  );
};

export default EventForm;
